import { memo, useMemo } from 'react';
import { Form } from '@formio/react';
import type { FormProps } from '@formio/react/lib/components/Form';
import { useTranslation } from '@hooks/useTranslation';
import { patchFileComponents } from '@lib/formioFileProvider';
import type { AuditEvent, AuditPrincipal, FieldChange } from '@/types/audit';
import { diffFormSchemas } from './formSchemaDiffUtil';
import styles from './FormSchemaPreview.module.scss';

interface FormSchemaPreviewProps {
  event: AuditEvent;
  change: FieldChange;
  side: 'before' | 'after';
  subject?: string;
  onBack: () => void;
  onToggleSide: () => void;
}

function FormSchemaPreviewInner({
  event,
  change,
  side,
  subject,
  onBack,
  onToggleSide,
}: FormSchemaPreviewProps) {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    const raw = side === 'before' ? change.beforeValue : change.afterValue;
    if (!raw || typeof raw !== 'object') return null;
    const clone = JSON.parse(JSON.stringify(raw)) as Record<string, unknown>;
    patchFileComponents(clone);
    return clone;
  }, [change, side]);

  const isEmpty =
    schema !== null &&
    (!Array.isArray(schema.components) || schema.components.length === 0);

  const diff = useMemo(
    () => diffFormSchemas(change.beforeValue, change.afterValue),
    [change],
  );

  const summary = t('K_AUDIT_FORM_DIFF_SUMMARY', '{a} added · {r} removed · {m} modified')
    .replace('{a}', String(diff.added.length))
    .replace('{r}', String(diff.removed.length))
    .replace('{m}', String(diff.modified.length));

  const title = humanizeEventName(event.eventName);
  const actor = actorName(event.actor) ?? t('K_AUDIT_ACTOR_USER', 'User');
  const timestamp = formatTimestamp(event.timestamp);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <button type="button" className={styles.back} onClick={onBack}>
          <span aria-hidden="true">←</span>
          {t('K_AUDIT_FORM_BACK_TO_LIST', 'Back to audit trail')}
        </button>

        {subject && <h2 className={styles.subject}>{subject}</h2>}

        <div className={styles.headerMeta}>
          <span>{title}</span>
          <span className={styles.metaDot}>·</span>
          <span>{actor}</span>
          <span className={styles.metaDot}>·</span>
          <span>{timestamp}</span>
        </div>

        <div className={styles.headerControls}>
          <span className={styles.viewingLabel}>
            {t('K_AUDIT_FORM_VIEWING', 'Viewing:')}
          </span>
          <div role="tablist" className={styles.toggle} data-side={side}>
            <span aria-hidden="true" className={styles.toggleThumb} />
            <button
              type="button"
              role="tab"
              aria-selected={side === 'before'}
              data-active={side === 'before'}
              className={styles.toggleButton}
              onClick={() => {
                if (side !== 'before') onToggleSide();
              }}
            >
              {t('K_AUDIT_FORM_SIDE_BEFORE', 'Before')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={side === 'after'}
              data-active={side === 'after'}
              className={styles.toggleButton}
              onClick={() => {
                if (side !== 'after') onToggleSide();
              }}
            >
              {t('K_AUDIT_FORM_SIDE_AFTER', 'After')}
            </button>
          </div>
          <span className={styles.summary}>{summary}</span>
        </div>
      </div>

      <div className={`${styles.formScroll} ${styles.fade}`} key={side}>
        {schema === null ? (
          <div className={styles.emptyState}>
            {t('K_AUDIT_FORM_NO_SCHEMA', 'No form schema available for this side.')}
          </div>
        ) : isEmpty ? (
          <div className={styles.emptyState}>
            {t('K_AUDIT_FORM_NO_FIELDS', 'This version of the form has no fields.')}
          </div>
        ) : (
          <div className={styles.formFrame}>
            <Form
              src={schema as unknown as FormProps['src']}
              options={{ readOnly: true, renderMode: 'form' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Memo: an audit event's schemas are immutable for the life of the event,
// so render output is fully determined by (event, change, side).
export const FormSchemaPreview = memo(FormSchemaPreviewInner);

function humanizeEventName(name: string): string {
  const lower = name.toLowerCase().replaceAll('_', ' ').trim();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function actorName(a: AuditPrincipal | undefined): string | null {
  if (!a) return null;
  if (typeof a.fullName === 'string' && a.fullName.trim().length > 0) return a.fullName.trim();
  const first = typeof a.firstName === 'string' ? a.firstName.trim() : '';
  const last = typeof a.lastName === 'string' ? a.lastName.trim() : '';
  const composed = [first, last].filter(Boolean).join(' ');
  if (composed) return composed;
  if (typeof a.name === 'string' && a.name.trim().length > 0) return a.name.trim();
  if (typeof a.email === 'string' && a.email.trim().length > 0) return a.email.trim();
  return null;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}
